// API Service Configuration and Base Client

import { logEvent as actionLogEvent } from '../utils/actionLog';
import { refreshAccessToken } from './tokenRefresh';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// #2115 — recover from an expired access token instead of failing the call.
// iOS freezes JS timers while a standalone PWA is suspended, so useAuth's
// timer-based refresh routinely misses expiry; before this, every API call
// then 401'd until the stale timer finally fired. On a 401 we run ONE
// single-flight refresh (concurrent 401s share it — see tokenRefresh.ts)
// and hand back the new token so the caller can replay the request once.
// Returns null when refresh fails: AUTH failure already cleared the session
// (rdm:session-expired → useAuth logout), and a TRANSIENT failure must
// surface the original 401 rather than loop.
async function recoverFromExpiredToken(hadToken: string | null, endpoint: string): Promise<string | null> {
  if (!hadToken || endpoint.startsWith('/api/auth/')) return null;
  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  try {
    // Token is stored directly as 'accessToken' by useAuth hook
    const token = localStorage.getItem('accessToken');
    if (token) {
      return token;
    }
  } catch {
    return null;
  }
  return null;
};

// Dispatch the maintenance event ONLY when the response is an explicit
// 503 carrying the `X-Maintenance: 1` header emitted by our nginx
// maintenance location block. Plain 503s (transient backend hiccup,
// upstream timeout, etc.) MUST NOT surface the maintenance overlay -
// they were causing false-positive overlays on bad cellular during
// field testing (#1943).
//
// Network-level failures (fetch throws, navigator offline, packet
// loss with radio still up) do NOT fire this event - those cases
// flow through useConnectionStatus() which surfaces the existing
// offline/syncing indicator without nuking React state. The previous
// heuristics here (opaque response, navigator.onLine on throw) were
// the source of the false-positive maintenance flicker.
function detectMaintenanceFromResponse(response: Response): void {
  if (response.status === 503 && response.headers.get('X-Maintenance') === '1') {
    window.dispatchEvent(new Event('rdm:maintenance'));
  }
}

// ---------------------------------------------------------------------------
// Real-time deploy detection via X-Build-Hash response header.
//
// The backend (apps/web/middleware/build_hash.py) stamps every response with
// `X-Build-Hash: <git-sha>`. We track the FIRST hash we see and compare
// against every subsequent response. When the hash changes, we know the
// backend has been redeployed — fire the `rdm:build-changed` event so the
// UpdatePrompt can trigger an immediate service-worker update instead of
// waiting for the periodic poll. This makes deploys propagate to running
// PWAs within ~1s of any API call rather than up to 5 minutes.
//
// The first response sets `knownBuildHash`; we don't fire on initial load.
// ---------------------------------------------------------------------------
let knownBuildHash: string | null = null;
function detectBuildChangeFromResponse(response: Response): void {
  const hash = response.headers.get('X-Build-Hash');
  if (!hash) return;
  if (knownBuildHash === null) {
    knownBuildHash = hash;
    return;
  }
  if (hash !== knownBuildHash) {
    const previous = knownBuildHash;
    knownBuildHash = hash;
    window.dispatchEvent(
      new CustomEvent('rdm:build-changed', { detail: { previous, current: hash } }),
    );
  }
}

// ---------------------------------------------------------------------------
// Maintenance probe (#2135).
//
// During a deploy window the maintenance gate 503s CORS PREFLIGHTS too —
// nginx 1.18 ignores the return-204-for-OPTIONS inside the error_page
// location — and a preflight must be 2xx, so every preflighted call (login
// POST, any JSON request) dies in the browser as a generic TypeError before
// detectMaintenanceFromResponse can ever see the 503. The PWA then showed a
// bare login error instead of the maintenance overlay.
//
// The probe is a SIMPLE request: bare GET, no custom headers → no preflight.
// The maintenance 503 for GETs carries X-Maintenance + Access-Control-
// Expose-Headers (verified live), so the probe can read the marker
// cross-origin and fire the overlay. A probe that itself throws means we're
// genuinely offline — stay silent and let the connection indicator handle
// it (#1943's contract).
// ---------------------------------------------------------------------------
let lastMaintenanceProbeAt = 0;

export async function probeMaintenance(): Promise<void> {
  const now = Date.now();
  if (now - lastMaintenanceProbeAt < 10_000) return; // throttle bursts
  lastMaintenanceProbeAt = now;
  try {
    const response = await fetch(`${BASE_URL}/api/diagnostics/ping/?_m=${now}`, { method: 'GET' });
    detectMaintenanceFromResponse(response);
  } catch {
    // Genuinely unreachable — offline indicator territory, not maintenance.
  }
}

/**
 * Thin wrapper around fetch() that fires the rdm:maintenance event on
 * network failure or 503 responses. Use this for code paths that can't
 * adopt apiFetch wholesale - e.g. auth endpoints that need different
 * header handling than apiFetch's default (no Bearer token on login,
 * different content-type on refresh).
 *
 * The caller still gets the raw Response (or the original thrown error)
 * - this wrapper only does the side-effect of dispatching the event,
 * it does not transform success/failure semantics.
 *
 * #1830
 */
export async function fetchWithMaintenanceDetection(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Network errors are NOT directly routed through the maintenance event —
  // they flow to the offline indicator via useConnectionStatus (#1943). But
  // a preflight blocked by the maintenance gate ALSO surfaces as a network
  // error (#2135), so on throw we fire the no-preflight probe, which only
  // raises the overlay when it can positively read X-Maintenance.
  let response: Response;
  try {
    response = await fetch(input, init);
  } catch (networkError) {
    void probeMaintenance();
    throw networkError;
  }
  detectBuildChangeFromResponse(response);
  detectMaintenanceFromResponse(response);
  return response;
}

// Base fetch wrapper with auth headers
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const method = (options.method as string | undefined) ?? 'GET';
  const startedAt = performance.now();
  // Strip query string from endpoint for the action-log entry — no
  // cache-busters or secret tokens in the log.
  const pathForLog = endpoint.split('?')[0];

  // Network errors propagate untouched - they are surfaced via the
  // offline / syncing indicator (useConnectionStatus), not via the
  // maintenance overlay. The maintenance event is only dispatched
  // from detectMaintenanceFromResponse() when nginx returns an
  // explicit X-Maintenance: 1 header. (#1943)
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (networkError) {
    actionLogEvent({
      kind: 'api',
      method,
      url: pathForLog,
      ms: Math.round(performance.now() - startedAt),
      note: 'network error',
    });
    // A blocked CORS preflight during a maintenance window surfaces here as
    // a network error — the no-preflight probe raises the overlay when the
    // gate is really up (#2135).
    void probeMaintenance();
    throw networkError;
  }

  // Real-time deploy detection — runs on every response (success or error)
  // so we catch redeploys even when API calls happen to fail.
  detectBuildChangeFromResponse(response);

  // Action log (#1895). Captures method + path + status + elapsed for
  // every API call so the rep's session can be replayed in the admin
  // diagnostics view. Skip the diagnostics bandwidth-test endpoint
  // since logging it would pollute every diagnostics submission.
  if (!pathForLog.endsWith('/diagnostics/bandwidth-test/') && !pathForLog.endsWith('/diagnostics/ping/')) {
    actionLogEvent({
      kind: 'api',
      method,
      url: pathForLog,
      status: response.status,
      ms: Math.round(performance.now() - startedAt),
    });
  }

  // #2115: expired access token → single-flight refresh → replay ONCE.
  if (response.status === 401) {
    const freshToken = await recoverFromExpiredToken(token, endpoint);
    if (freshToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${freshToken}`;
      const retryStartedAt = performance.now();
      response = await fetch(`${BASE_URL}${endpoint}`, { ...options, headers });
      detectBuildChangeFromResponse(response);
      actionLogEvent({
        kind: 'api',
        method,
        url: pathForLog,
        status: response.status,
        ms: Math.round(performance.now() - retryStartedAt),
        note: 'retry after token refresh',
      });
    }
  }

  if (!response.ok) {
    detectMaintenanceFromResponse(response);
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(response.status, extractApiErrorMessage(errorData) || response.statusText, errorData);
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// For file uploads (multipart/form-data)
async function apiUpload<T>(
  endpoint: string,
  formData: FormData,
  method: 'POST' | 'PUT' = 'POST'
): Promise<T> {
  const token = getAuthToken();

  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Don't set Content-Type for FormData - browser will set it with boundary

  // Network errors propagate after firing the maintenance probe — a blocked
  // CORS preflight during a deploy window looks identical to a network
  // failure (#2135); the probe disambiguates.
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${endpoint}`, {
      method,
      headers,
      body: formData,
    });
  } catch (networkError) {
    void probeMaintenance();
    throw networkError;
  }

  detectBuildChangeFromResponse(response);

  // #2115: same expired-token recovery as apiFetch — photo/doc uploads after
  // an iOS resume are exactly the calls that used to die on a stale token.
  if (response.status === 401) {
    const freshToken = await recoverFromExpiredToken(token, endpoint);
    if (freshToken) {
      headers['Authorization'] = `Bearer ${freshToken}`;
      response = await fetch(`${BASE_URL}${endpoint}`, { method, headers, body: formData });
      detectBuildChangeFromResponse(response);
    }
  }

  if (!response.ok) {
    detectMaintenanceFromResponse(response);
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(response.status, extractApiErrorMessage(errorData) || response.statusText, errorData);
  }

  return response.json();
}

/**
 * Pull a human-readable error message out of a DRF / Django error
 * response. Previously we only looked at `errorData.detail`, which is
 * what auth/permission errors use — but serializer validation errors
 * arrive as either `non_field_errors: [...]` or `<fieldname>: [...]`,
 * which would never surface. Result: the rep saw "Bad Request" instead
 * of "Start miles and End miles are the same" (caught in diag #20).
 *
 * Order: detail → non_field_errors → first field-level error → message
 */
function extractApiErrorMessage(errorData: unknown): string | null {
  if (!errorData || typeof errorData !== 'object') return null;
  const e = errorData as Record<string, unknown>;

  if (typeof e.detail === 'string' && e.detail) return e.detail;

  const nfe = e.non_field_errors;
  if (Array.isArray(nfe) && nfe.length > 0 && typeof nfe[0] === 'string') return nfe[0];

  if (typeof e.message === 'string' && e.message) return e.message;
  if (typeof e.error === 'string' && e.error) return e.error;

  // Field-level errors: { fieldname: ["error 1", "error 2"], ... }
  // Surface the first one with the field name so the rep knows what to fix.
  for (const [key, value] of Object.entries(e)) {
    if (key === 'detail' || key === 'non_field_errors' || key === 'message' || key === 'error') continue;
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return `${key}: ${value[0]}`;
    }
    if (typeof value === 'string' && value) {
      return `${key}: ${value}`;
    }
  }
  return null;
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, message: string, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// =============================================================================
// TYPES
// =============================================================================

export interface Associate {
  associateid: number;
  firstname: string;
  lastname: string;
  email: string;
  active: boolean;
}

export interface Store {
  storeid: number;
  legacystoreid: number;
  storename: string;
  storenumber: string;
  // Address fields from backend
  storeaddress1: string;
  storeaddress2?: string;
  storecity: string;
  storestate: string;
  storezip: string;
  // Computed convenience fields
  address: string;  // Computed from storeaddress1
  city: string;     // Computed from "city, state zip"
  state: string;
  zip: string;
  phone: string;
  latitude: number;  // From backend lat
  longitude: number; // From backend lon
  retailerid?: number;
  retailername?: string;
}

export interface Project {
  projectid: number;
  projectname: string;
  projectdesc: string;
  active: boolean;
  clientid: number;
  clientname: string;
}

export interface ProjectStore {
  projectid: number;
  storeid: number;
  store: Store;
  routeorder: number;
}

export interface CallLog {
  // Core identifiers
  calllogid: number;
  associate_id: number;
  store_legacystoreid: number; // This is what API actually returns
  project_id: number | null;

  // Date/time fields (from API)
  dtecall: string; // ISO date
  stcalltime: string | null; // Call start time
  edcalltime: string | null; // Call end time
  stdrivetime: string | null; // Drive start time
  eddrivetime: string | null; // Drive end time
  stdrivetimeback: string | null;
  eddrivetimeback: string | null;

  // Mileage
  driveodostart: number | null;
  driveodoend: number | null;
  drivebackodostart: number | null;
  drivebackodoend: number | null;
  totalmiles: number | null;
  totalmilesback: number | null;
  totaltime: string | null;

  // Store info (denormalized)
  store_city: string;
  retailer_abbreviation: string;
  description_of_activity: string;
  project_description?: string; // Annotated from backend query

  // Other fields
  notes: string | null;
  overnight: boolean;
  teamproject: boolean;
  teamleader: boolean;
  timezone: string | null;

  // GPS (optional - added for future use)
  lat?: number | null;
  lon?: number | null;
  gps_accuracy?: number | null;
  gps_override_reason?: string | null;
  unverified_reason_code?: string | null;

  // Checkout signature fields
  manager_name?: string | null;
  signature?: string | null;
  no_signature_reason?: string | null;
  has_signature?: boolean;  // Computed field from API - true if signature or no_signature_reason exists

  // Edit audit fields
  edited_after_signature?: boolean;
  edit_timestamp?: string | null;
  edit_reason?: string | null;
}

// Document/Photo record from the unified documents API
export interface DocumentPhoto {
  record_id: number;
  record_type: 'documentdetails' | 'photodetails';
  project_id: number | null;
  store_id: number | null;
  doc_date: string | null;
  filename: string;
  blob_url: string;
  has_thumbnail: boolean;
  associate_id: number | null;
}

export interface CallLogCreate {
  store_id: number;
  project_id: number;
  associate_id: number;
  dtecall: string;  // Call date in YYYY-MM-DD or ISO format
  // Drive-to-store fields
  stdrivetime?: string;
  eddrivetime?: string;
  driveodostart?: number;
  driveodoend?: number;
  // Return drive fields (from last checkout to end of day)
  stdrivetimeback?: string;
  eddrivetimeback?: string;
  drivebackodostart?: number;
  drivebackodoend?: number;
  // Store visit times
  stcalltime?: string;  // Call time in (check-in time)
  edcalltime?: string;  // Call time out (check-out time)
  notes?: string;
  // Why this visit exists off the rep's schedule — its own column, because an
  // audit fact must survive the checkout notes rewrite.
  off_schedule_reason?: string;
  overnight?: boolean;
  teamproject: boolean;
  teamleader: boolean;
  // GPS fields for location tracking
  lat?: number;
  lon?: number;
  gps_accuracy?: number;
  // GPS override reason when checking in outside tolerance
  gps_override_reason?: string;
  // System classification of why presence could not be verified (off_site, gps_low_accuracy, gps_unavailable, store_missing_coords)
  unverified_reason_code?: string;
}

export interface SurveyQuestion {
  qid: number;
  question: string;
  qtypeid: number;
  mandatory: boolean;
  disporder: number;
  options: SurveyOption[];
}

export interface SurveyOption {
  optionid: number;
  optionvalue: string;
  disporder: number;
}

export interface Survey {
  surveyid: number;
  surveytitle: string;
  surveydesc: string;
  active: boolean;
  questions: SurveyQuestion[];
}

export interface SurveyResponseSubmit {
  surveyid: number;
  calllogid: number;
  responses: { qid: number; responseText: string }[];
  urgentReview?: boolean;
}

// Survey response detail (individual answer)
export interface SurveyResponseDetail {
  responsedetailid: number;
  responseid: number;
  qid: number;
  question_text: string;
  textid: number | null;
  response_text: string | null;
}

// Complete survey response with all answers
export interface SurveyResponse {
  responseid: number;
  respondant: string;
  userid: number | null;
  user_name: string | null;
  dtresponse: string | null;
  keylink: number | null; // Call log ID
  surveyid: number;
  survey_title: string;
  response_details: SurveyResponseDetail[];
}

export interface Product {
  productid: number;
  productname: string;
  sku: string;
  price: number;
  active: boolean;
}

export interface OrderItem {
  productid: number;
  quantity: number;
  price?: number;
  notes?: string;
}

export interface OrderCreate {
  projectid: number;
  storeid: number;
  associateid: number;
  products: OrderItem[];
  notes?: string;
}

export interface Order {
  orderid: number;
  confirmation: number;
  status: 'OPEN' | 'CLOSED' | 'CANCELLED' | 'RETURNED';
  order_number: string;
  project: string | number;
  store_number: number | string;
  retailer_name: string;
  client_name: string;
  distributor_name: string;
  associate: string;
  entered_on: string;
  amount: number;
  pieces: number;
}

// =============================================================================
// API SERVICES
// =============================================================================

// Associates
export const associatesApi = {
  getCurrentAssociate: () =>
    apiFetch<Associate>('/api/associates/associates/'),

  updateProfile: (data: Partial<Associate>) =>
    apiFetch<Associate>('/api/associates/associates/', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  uploadSignature: (signatureData: string, signatureSvg?: string) => {
    const formData = new FormData();
    // Convert base64 data URL to blob
    const base64Data = signatureData.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    const blob = new Blob([new Uint8Array(byteArrays)], { type: 'image/png' });
    formData.append('signature', blob, 'signature.png');
    if (signatureSvg) {
      formData.append('signature_svg', signatureSvg);
    }
    return apiUpload<{ success: boolean }>('/api/associates/associates/upload-signature/', formData);
  },
};

// Projects
export const projectsApi = {
  getProjects: (signal?: AbortSignal) =>
    apiFetch<Project[]>('/api/projects/projects/', { signal }),

  getProject: (id: number) =>
    apiFetch<Project>(`/api/projects/projects/${id}/`),

  getProjectSets: (dateRange: string) =>
    apiFetch<Project[]>(`/api/projects/projects/project-sets/?date_range=${dateRange}`),

  // #2675 — projects flagged always_available, pinned to the Today screen so a
  // rep/DM can log time against them via Manual Entry without a scheduled route.
  getAlwaysAvailableProjects: (signal?: AbortSignal) =>
    apiFetch<Project[]>('/api/projects/projects/always-available/', { signal }),
};

// #2975 — rep availability + PTO (shared API with the Django profile).
export interface AvailabilityWindow {
  id: number;
  day_of_week: number;
  start_time: string | null;
  end_time: string | null;
  max_hours: string | null;
}
export interface AvailabilityTimeOff {
  id: number;
  start_date: string;
  end_date: string;
  kind: string;
  reason: string;
}
export interface AvailabilityRequestItem {
  id: number;
  request_type: 'availability' | 'time_off';
  payload: Record<string, unknown>;
  note: string;
  status: 'pending' | 'approved' | 'rejected' | 'superseded' | 'withdrawn';
  created_at: string;
}
export interface MyAvailability {
  associate_id: number;
  weekly_min_hours: string | null;
  weekly_max_hours: string | null;
  availability: AvailabilityWindow[];
  time_off: AvailabilityTimeOff[];
  pending_availability: AvailabilityRequestItem | null;
  requests: AvailabilityRequestItem[];
}

export const availabilityApi = {
  getMine: () => apiFetch<MyAvailability>('/api/associates/availability/me/'),
  createRequest: (body: { request_type: 'availability' | 'time_off'; payload: unknown; note?: string }) =>
    apiFetch<AvailabilityRequestItem>('/api/associates/availability/request/', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  withdrawRequest: (id: number) =>
    apiFetch<AvailabilityRequestItem>(`/api/associates/availability/requests/${id}/withdraw/`, {
      method: 'POST',
    }),
};

// #2986 — rep self-service contact + address (shared API with the Django profile).
// Saving an address re-geocodes and sets the home timezone server-side.
export interface MyContact {
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  telephone: string | null;
  cell: string | null;
  lat: string | null;
  lon: string | null;
  geocode_status: string | null;
  timezone: string;
}

export const contactApi = {
  getMine: () => apiFetch<MyContact>('/api/associates/me/contact/'),
  update: (body: Partial<Pick<MyContact, 'address1' | 'address2' | 'city' | 'state' | 'zip' | 'telephone' | 'cell'>>) =>
    apiFetch<MyContact>('/api/associates/me/contact/', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// Store Note type
export interface StoreNote {
  id: number;
  storeId: number;
  authorName: string;
  authorId: number;
  content: string;
  createdAt: string;
  isPinned: boolean;
}

// Stores
export const storesApi = {
  getStores: () =>
    apiFetch<Store[]>('/api/retailers/stores/'),

  getStore: async (storeId: number, projectId?: number): Promise<Store> => {
    // Backend returns { status: 'success', data: {...} }
    // projectId helps disambiguate when legacystoreid is not unique across retailers
    interface BackendStore {
      storeid: number;
      legacystoreid: number;
      storename: string;
      storenumber: string;
      storeaddress1: string;
      storeaddress2?: string;
      storecity: string;
      storestate: string;
      storezip: string;
      lat: number | null;
      lon: number | null;
    }
    const params = projectId ? `?projectId=${projectId}` : '';
    const response = await apiFetch<{ status: string; data: BackendStore }>(`/api/retailers/stores/${storeId}/${params}`);
    const store = response.data;
    // Transform to frontend Store format
    return {
      storeid: store.storeid,
      legacystoreid: store.legacystoreid,
      storename: store.storename,
      storenumber: store.storenumber || String(store.legacystoreid),
      storeaddress1: store.storeaddress1 || '',
      storeaddress2: store.storeaddress2,
      storecity: store.storecity || '',
      storestate: store.storestate || '',
      storezip: store.storezip || '',
      // Computed fields for backward compatibility
      address: store.storeaddress1 || '',
      city: `${store.storecity || ''}, ${store.storestate || ''} ${store.storezip || ''}`.trim(),
      state: store.storestate || '',
      zip: store.storezip || '',
      phone: '', // Not in backend response
      latitude: store.lat || 0,
      longitude: store.lon || 0,
    };
  },

  getProjectStore: (projectId: number, storeId: number) =>
    apiFetch<ProjectStore>(`/api/retailers/stores/project-stores/${projectId}/${storeId}/`),

  // Look up store by project ID and legacy store ID (for unassigned call entry)
  getProjectStores: async (projectId: number, legacyStoreId: number): Promise<{
    storeid: number;
    legacystoreid: number;
    storename: string;
    storenumber: string;
    storeaddress1: string;
    storecity: string;
    storestate: string;
    storezip: string;
    retailerid?: {
      abbreviation?: string;
      retailername?: string;
    };
  }[]> => {
    interface ProjectStoreResponse {
      status: string;
      message: string;
      data: {
        storeid: number;
        legacystoreid: number;
        storename: string;
        storenumber: string;
        storeaddress1: string;
        storeaddress2?: string;
        storecity: string;
        storestate: string;
        storezip: string;
        retailerid?: {
          abbreviation?: string;
          retailername?: string;
        };
      }[];
    }
    const response = await apiFetch<ProjectStoreResponse>(`/api/retailers/stores/project-stores/${projectId}/${legacyStoreId}/`);
    return response.data || [];
  },

  // Store Notes
  getStoreNotes: (storeId: number) =>
    apiFetch<StoreNote[]>(`/api/retailers/stores/${storeId}/notes/`),

  addStoreNote: (storeId: number, content: string) =>
    apiFetch<StoreNote>(`/api/retailers/stores/${storeId}/notes/`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),

  deleteStoreNote: (storeId: number, noteId: number) =>
    apiFetch<void>(`/api/retailers/stores/${storeId}/notes/${noteId}/`, {
      method: 'DELETE',
    }),

  // Update store coordinates (from geocoding)
  updateStoreCoordinates: (storeId: number, lat: number, lng: number) =>
    apiFetch<void>(`/api/retailers/stores/${storeId}/coordinates/`, {
      method: 'PATCH',
      body: JSON.stringify({ lat, lon: lng }),
    }),

  // Get stores associated with a project (via project sets/routes)
  getStoresByProject: async (projectId: number): Promise<{
    id: number;
    legacystoreid: number;
    name: string;
    address: string;
    retailer_abbreviation: string;
    storenumber: string;
  }[]> => {
    // Query project sets to get unique stores for this project
    interface ProjectSetStore {
      storeid: number;
      store_id?: number;
      legacystoreid?: number;
      storename?: string;
      store_name?: string;
      storenumber?: string;
      store_number?: string;
      storeaddress1?: string;
      address?: string;
      storecity?: string;
      city?: string;
      storestate?: string;
      state?: string;
      retailer_abbreviation?: string;
      retailerid__retailerabbreviation?: string;
    }
    interface ProjectSetResponse {
      storeid: ProjectSetStore;
    }
    try {
      const data = await apiFetch<ProjectSetResponse[]>(`/api/projects/projects/${projectId}/project-stores/`);
      // Extract unique stores from project sets
      const storeMap = new Map<number, {
        id: number;
        legacystoreid: number;
        name: string;
        address: string;
        retailer_abbreviation: string;
        storenumber: string;
      }>();

      for (const ps of data) {
        const store = ps.storeid;
        if (!store) continue;
        const storeId = store.storeid || store.store_id;
        if (!storeId || storeMap.has(storeId)) continue;

        storeMap.set(storeId, {
          id: storeId,
          legacystoreid: store.legacystoreid || storeId,
          name: store.storename || store.store_name || '',
          address: `${store.storeaddress1 || store.address || ''}, ${store.storecity || store.city || ''}, ${store.storestate || store.state || ''}`.replace(/^, |, $/g, ''),
          retailer_abbreviation: store.retailer_abbreviation || store.retailerid__retailerabbreviation || '',
          storenumber: store.storenumber || store.store_number || '',
        });
      }

      return Array.from(storeMap.values()).sort((a, b) => a.legacystoreid - b.legacystoreid);
    } catch (err) {
      console.error('Failed to get stores by project:', err);
      return [];
    }
  },

  // Typeahead search for stores - up to 50 matches by legacystoreid prefix
  // (numeric query) or by retailer abbreviation / address (alpha query).
  // Used by manual entry's bidirectional picker when the rep wants to start
  // with the STORE side and projects aren't selected yet. #1851
  searchStores: async (query: string): Promise<Array<{
    id: number;
    legacystoreid: number;
    name: string;
    address: string;
    retailer_abbreviation: string;
    storenumber: string;
  }>> => {
    const q = query.trim();
    if (!q) return [];
    try {
      interface BackendStore {
        storeid: number;
        legacystoreid: number;
        storenumber: string;
        storename: string;
        storeaddress1: string;
        storecity: string;
        storestate: string;
        retailer_abbreviation: string;
      }
      const data = await apiFetch<BackendStore[]>(
        `/api/retailers/stores/search/?q=${encodeURIComponent(q)}`
      );
      return data.map((s) => ({
        id: s.storeid,
        legacystoreid: s.legacystoreid,
        name: s.storename || '',
        address: `${s.storeaddress1 || ''}${s.storecity ? `, ${s.storecity}` : ''}${s.storestate ? `, ${s.storestate}` : ''}`.replace(/^, |, $/g, ''),
        retailer_abbreviation: s.retailer_abbreviation || '',
        storenumber: s.storenumber || '',
      }));
    } catch (err) {
      console.error('Store search failed:', err);
      return [];
    }
  },

  // Inverse of getStoresByProject: list projects with routes at a given
  // store. Used by manual entry's bidirectional store/project picker (#1851).
  getProjectsByStore: async (storeId: number): Promise<Project[]> => {
    try {
      interface BackendProject {
        projectid: number;
        projectdesc: string | null;
      }
      const data = await apiFetch<BackendProject[]>(
        `/api/retailers/stores/${storeId}/store-projects/`
      );
      return data.map((p) => ({
        projectid: p.projectid,
        projectdesc: p.projectdesc || '',
        projectname: p.projectdesc || '',
        active: true,
        clientid: 0,
        clientname: '',
      }));
    } catch (err) {
      console.error('Failed to get projects by store:', err);
      return [];
    }
  },
};

// Store Manager History API
export interface StoreManagerHistory {
  id: number;
  store: number;
  manager_name: string;
  last_used: string;
  use_count: number;
}

// Paginated response type (DRF default pagination)
interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const storeManagersApi = {
  // Get manager history for a store
  getManagers: async (storeId: number): Promise<StoreManagerHistory[]> => {
    // The endpoint requires a valid numeric store_id (400 otherwise). The
    // manager autocomplete is optional, so skip the call for a missing /
    // NaN / non-positive id rather than emit a spurious 400 into the
    // network log (diag #37 / #1996).
    if (!Number.isFinite(storeId) || storeId <= 0) return [];
    const response = await apiFetch<PaginatedResponse<StoreManagerHistory> | StoreManagerHistory[]>(
      `/api/retailers/store-managers/?store_id=${storeId}`
    );
    // Handle both paginated and non-paginated responses
    if (Array.isArray(response)) {
      return response;
    }
    return response.results || [];
  },

  // Add a new manager to history (or update existing)
  addManager: (storeId: number, managerName: string) =>
    apiFetch<StoreManagerHistory>('/api/retailers/store-managers/', {
      method: 'POST',
      body: JSON.stringify({ store: storeId, manager_name: managerName }),
    }),
};

// Call Log API response structure (nested)
interface CallLogApiResponse {
  status: string;
  message: string;
  data: {
    associate: {
      associateid: number;
      name: string;
      supervisor: string;
      week_end_date: string;
    };
    totals: {
      totaldrivetime: string;
      totaltime: string;
      totalmiles: number;
      totalexpenses: number;
    };
    expenses: unknown[];
    data: CallLog[];
  };
}

// Call Logs (Time Tracking / Visits)
export const callLogsApi = {
  getCallLogs: async (date?: string, associateIds?: number[]): Promise<CallLog[]> => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (associateIds?.length) params.append('associate_ids', associateIds.join(','));
    // RDM App always wants pending/scheduled entries - legacy apps don't pass this
    params.append('include_pending', 'true');

    // API returns nested structure, extract the call logs array
    const response = await apiFetch<CallLogApiResponse[]>(`/api/projects/calllog/?${params.toString()}`);

    // Extract call logs from nested structure
    if (Array.isArray(response) && response.length > 0) {
      return response[0]?.data?.data || [];
    }
    return [];
  },

  /**
   * Create a CallLog. Pass `idempotencyKey` (e.g. uuid v4) on retryable
   * mutations so the server can deduplicate — see #1867 / apps/projects/
   * apis/view.py CallLogViewSet.create. The key is forwarded as an
   * Idempotency-Key request header; same key + same associate returns the
   * existing CallLog with 200 OK instead of creating a duplicate.
   */
  createCallLog: (
    data: CallLogCreate,
    options?: { signal?: AbortSignal; idempotencyKey?: string },
  ) => {
    const extraHeaders: HeadersInit | undefined = options?.idempotencyKey
      ? { 'Idempotency-Key': options.idempotencyKey }
      : undefined;
    return apiFetch<CallLog>('/api/projects/calllog/', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      ...(extraHeaders ? { headers: extraHeaders } : {}),
    });
  },

  updateCallLog: (id: number, data: Partial<CallLogCreate>) =>
    apiFetch<CallLog>(`/api/projects/calllog/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Partial update using PATCH - use backend field names (edcalltime, stcalltime, etc.)
  patchCallLog: (id: number, data: Record<string, unknown>) =>
    apiFetch<CallLog>(`/api/projects/calllog/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Reopen a completed visit by clearing checkout time
  reopenVisit: (id: number) =>
    apiFetch<CallLog>(`/api/projects/calllog/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ edcalltime: null }),
    }),

  deleteCallLog: (id: number) =>
    apiFetch<void>(`/api/projects/calllog/${id}/`, {
      method: 'DELETE',
    }),

  voidCallLog: (id: number, voidReason: string) =>
    apiFetch<{ status: string; message: string }>(`/api/projects/calllog/${id}/void/`, {
      method: 'PATCH',
      body: JSON.stringify({ void_reason: voidReason }),
    }),

  /**
   * Chronological drive + store-visit + drive-back segments for the current
   * rep on a given date. Powers the consolidated manual-entry pre-flight
   * (#1863 / #1864) so the rep can see what's already booked and the form
   * can validate that a new entry doesn't overlap existing work.
   *
   * Returns segments in chronological order, with null `end_time` indicating
   * an in-flight (not-yet-checked-out) visit. Voided CallLogs are excluded
   * server-side via the .active() manager.
   */
  getDayTimeline: (date: string, signal?: AbortSignal) =>
    apiFetch<DayTimelineResponse>(
      `/api/projects/calllog/day-timeline/?date=${encodeURIComponent(date)}`,
      { signal },
    ),
};

export type DayTimelineEntryType = 'drive' | 'store_visit' | 'drive_back';

export interface DayTimelineEntry {
  type: DayTimelineEntryType;
  calllog_id: number;
  start_time: string;          // ISO datetime
  end_time: string | null;     // ISO datetime; null = in-flight
  /** Drive segments only. */
  start_odometer?: number | null;
  end_odometer?: number | null;
  context?: string;
  /** Store-visit segments only. */
  store_id?: number;
  legacy_store_id?: number;
  store_name?: string;
  city?: string | null;
  project_id?: number;
  project_name?: string;
}

export interface DayTimelineResponse {
  date: string; // YYYY-MM-DD
  associate_id: number;
  entries: DayTimelineEntry[];
}

// Documents & Photos
export const documentsApi = {
  uploadDocument: (
    projectId: number,
    storeId: number,
    files: File[],
    calllogId?: number,
    date?: string
  ) => {
    const formData = new FormData();
    formData.append('project_id', projectId.toString());
    formData.append('store_id', storeId.toString());
    if (calllogId) formData.append('calllog_id', calllogId.toString());
    if (date) formData.append('date', date);
    files.forEach((file) => formData.append('documents', file));
    return apiUpload<{ success: boolean; document_ids: number[] }>(
      '/api/projects/detaildocuments/upload/',
      formData
    );
  },

  uploadPhoto: (
    projectId: number,
    storeId: number,
    files: File[],
    calllogId?: number,
    date?: string
  ) => {
    const formData = new FormData();
    formData.append('project_id', projectId.toString());
    formData.append('store_id', storeId.toString());
    if (calllogId) formData.append('calllog_id', calllogId.toString());
    if (date) formData.append('date', date);
    files.forEach((file) => formData.append('documents', file));
    return apiUpload<{ success: boolean; document_ids: number[] }>(
      '/api/projects/detaildocuments/upload-photo/',
      formData
    );
  },

  getDocuments: (params: {
    startDate?: string;
    endDate?: string;
    associateIds?: number[];
    calllogIds?: number[];
    documentTypes?: ('photos' | 'documents')[];
  }) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    if (params.associateIds?.length) queryParams.append('associate_ids', params.associateIds.join(','));
    if (params.calllogIds?.length) queryParams.append('calllog_ids', params.calllogIds.join(','));
    if (params.documentTypes?.length) queryParams.append('document_types', params.documentTypes.join(','));
    return apiFetch<unknown[]>(`/api/projects/detaildocuments/?${queryParams.toString()}`);
  },

  // Get all documents and photos for a specific call log
  getByCallLog: (calllogId: number) =>
    apiFetch<{
      status: string;
      data: DocumentPhoto[];
      calllog_signed: boolean;
    }>(`/api/projects/detaildocuments/by-calllog/${calllogId}/`),

  // Delete a document or photo (only allowed for unsigned call logs)
  deleteRecord: (recordType: 'documentdetails' | 'photodetails', recordId: number) =>
    apiFetch<void>(`/api/projects/detaildocuments/delete/${recordType}/${recordId}/`, {
      method: 'DELETE',
    }),
};

// Surveys
export const surveysApi = {
  getSurveys: () =>
    apiFetch<Survey[]>('/api/surveys/'),

  getSurvey: (id: number) =>
    apiFetch<Survey>(`/api/surveys/${id}/`),

  getSurveyByProject: (projectId: number) =>
    apiFetch<Survey>(`/api/surveys/project/${projectId}/`),

  getSurveyQuestions: (surveyId: number) =>
    apiFetch<SurveyQuestion[]>(`/api/surveys/${surveyId}/questions/`),

  submitSurveyResponse: (data: SurveyResponseSubmit) =>
    // The server's QuestionResponseSerializer expects snake_case `response_text`
    // per-answer. The hooks/components throughout the PWA carry `responseText`
    // internally; the transform happens here at the wire boundary so every
    // caller doesn't have to remember to convert. (#1471 follow-up #9 —
    // was silently failing with 400 on every multi-task checkout.)
    apiFetch<{ success: boolean }>('/api/surveys/responses/submit/', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        responses: data.responses.map(({ qid, responseText }) => ({
          qid,
          response_text: responseText,
        })),
      }),
    }),

  // Get survey responses for a specific call log
  getResponsesByCallLog: (calllogId: number) =>
    apiFetch<{
      status: string;
      data: SurveyResponse[];
      calllog_signed: boolean;
    }>(`/api/surveys/responses/by-calllog/${calllogId}/`),

  // Update a specific survey answer (only for unsigned call logs)
  updateAnswer: (responseDetailId: number, responseText: string) =>
    apiFetch<{ status: string; data: SurveyResponseDetail }>(`/api/surveys/responses/update-answer/${responseDetailId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ response_text: responseText }),
    }),

  // Delete a survey response (only for unsigned call logs)
  deleteResponse: (responseId: number) =>
    apiFetch<void>(`/api/surveys/responses/delete-response/${responseId}/`, {
      method: 'DELETE',
    }),
};

// UPC Lookup result
export interface UpcLookupResult {
  productid: number;
  description: string;
  upc: string | null;
  codenbr: string | null;
  packsize: string | null;
  brand: string | null;
  size: string | null;
  uom: string | null;
  cost: number | null;
  distributor: {
    id: number;
    name: string;
  } | null;
  retailer: {
    id: number;
    name: string;
  } | null;
}

// Products & Orders
export const productsApi = {
  getProductsByProject: (projectId: number, storeId?: number, associateId?: number) => {
    const params = new URLSearchParams();
    if (storeId) params.append('storeid', storeId.toString());
    if (associateId) params.append('associateid', associateId.toString());
    return apiFetch<Product[]>(`/api/distributors/products/by-project/${projectId}/?${params.toString()}`);
  },

  // Generic UPC lookup - searches across all distributors/retailers
  lookupByUpc: async (upc: string): Promise<UpcLookupResult[]> => {
    const response = await apiFetch<{
      status: string;
      message: string;
      data: UpcLookupResult[];
      count: number;
    }>(`/api/distributors/products/lookup-by-upc/?upc=${encodeURIComponent(upc)}`);
    return response.data || [];
  },
};

export const ordersApi = {
  getOrders: (params?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    storeId?: number;
    projectId?: number;
    associateId?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('start_date', params.startDate);
    if (params?.endDate) queryParams.append('end_date', params.endDate);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.storeId) queryParams.append('store_id', params.storeId.toString());
    if (params?.projectId) queryParams.append('project_id', params.projectId.toString());
    if (params?.associateId) queryParams.append('associate_id', params.associateId.toString());
    return apiFetch<Order[]>(`/api/orders/?${queryParams.toString()}`);
  },

  createOrder: (data: OrderCreate) =>
    apiFetch<Order>('/api/orders/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getOrder: (id: number) =>
    apiFetch<Order>(`/api/orders/${id}/`),

  updateOrderStatus: (id: number, status: Order['status']) =>
    apiFetch<Order>(`/api/orders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  addOrderNote: (id: number, note: string) =>
    apiFetch<{ success: boolean }>(`/api/orders/${id}/notes/`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
};

// Expense Types
export interface ExpenseType {
  expenseid: number;
  expensename: string;
  description?: string;
}

// Expense Log — server returns snake_case fields, normalised to camelCase here.
export interface ExpenseLog {
  expenselogid: number;
  associateid: number;
  expenseid: number;
  date: string;
  amount: number;
  description: string | null;
  filename: string | null;
  blobUrl: string | null;
  associateName: string;
  expenseName: string;
}

// Raw server shape — `expense_name` etc. snake_case.
interface RawExpenseLog {
  expenselogid: number;
  associateid: number;
  expenseid: number;
  date: string;
  amount: number;
  description: string | null;
  filename: string | null;
  blob_url: string | null;
  associate_name: string;
  expense_name: string;
}

const normaliseExpenseLog = (r: RawExpenseLog): ExpenseLog => ({
  expenselogid: r.expenselogid,
  associateid: r.associateid,
  expenseid: r.expenseid,
  date: r.date,
  amount: typeof r.amount === 'string' ? parseFloat(r.amount) : r.amount,
  description: r.description,
  filename: r.filename,
  blobUrl: r.blob_url,
  associateName: r.associate_name,
  expenseName: r.expense_name,
});

// Expense Logs
export const expensesApi = {
  // Get expense types/categories
  getExpenseTypes: async (): Promise<ExpenseType[]> => {
    const response = await apiFetch<[{ data: ExpenseType[] }]>('/api/associates/expenses/');
    return response[0]?.data || [];
  },

  // Get expense logs for a date (date is the Friday of the week)
  getExpenseLogs: async (date: string): Promise<ExpenseLog[]> => {
    const response = await apiFetch<[{ data: RawExpenseLog[] }]>(`/api/associates/expense-logs/?date=${date}`);
    return (response[0]?.data || []).map(normaliseExpenseLog);
  },

  // Create expense log with optional file upload
  createExpenseLog: (data: {
    expenseId: number;
    date: string;
    amount: number;
    description?: string;
    document?: File;
  }) => {
    const formData = new FormData();
    formData.append('expense_id', data.expenseId.toString());
    formData.append('date', data.date);
    formData.append('amount', data.amount.toString());
    if (data.description) formData.append('description', data.description);
    if (data.document) formData.append('document', data.document);
    return apiUpload<{ success: boolean }>('/api/associates/expense-logs/', formData);
  },

  // Delete expense log
  deleteExpenseLog: (id: number) =>
    apiFetch<void>(`/api/associates/expense-logs/${id}/`, {
      method: 'DELETE',
    }),
};

// Document Category and Downloads
export interface DocumentCategory {
  categoryid: number | string;
  categoryname: string;
}

export interface CategoryDocument {
  fileid: number;
  filename: string;
  description: string | null;
  filelocation: string | null;
}

export const categoryDocumentsApi = {
  // Get document categories (folders)
  getCategories: async (): Promise<DocumentCategory[]> => {
    // API returns: [{"status":"success","data":[{categoryid, categorydescription}, ...]}]
    const response = await apiFetch<
      | { status: string; data: { categoryid: number; categorydescription: string }[] }[]
      | { count: number; results: { categoryid: number; categorydescription: string }[] }
    >('/api/projects/categories/');

    // Handle array wrapper format
    let categories: { categoryid: number; categorydescription: string }[] = [];
    if (Array.isArray(response) && response[0]?.data) {
      categories = response[0].data;
    } else if ('results' in response) {
      categories = response.results || [];
    }

    return categories.map((item) => ({
      categoryid: item.categoryid,
      categoryname: (item.categorydescription || '').trim(),
    }));
  },

  // Get documents in a category
  getDocuments: async (
    categoryId: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ data: CategoryDocument[]; pagination: { count: number; pageSize: number } }> => {
    // Backend returns nested structure: { status, message, data: { data: [...], pagination: {...} } }
    const response = await apiFetch<{
      status: string;
      message: string;
      data: {
        data: CategoryDocument[];
        pagination: { count: number; next: string | null; previous: string | null };
      };
    }>(`/api/projects/documents/category/${categoryId}/?page=${page}&page_size=${pageSize}`);
    return {
      data: response.data?.data || [],
      pagination: {
        count: response.data?.pagination?.count || 0,
        pageSize: pageSize,
      },
    };
  },

  // Download a document (returns blob)
  downloadDocument: async (fileId: number): Promise<Blob> => {
    const token = getAuthToken();
    const response = await fetch(`${BASE_URL}/api/projects/documents/${fileId}/download/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error('Download failed');
    }
    return response.blob();
  },

  // Stream a media file (audio/video) - returns Azure SAS URL for direct streaming
  streamDocument: async (fileId: number): Promise<string> => {
    // Use streaming endpoint that returns a SAS URL for direct Azure streaming
    // This enables HTTP Range requests for true streaming (no full download needed)
    const response = await apiFetch<{
      status: string;
      stream_url: string;
      filename: string;
      mime: string | null;
    }>(`/api/projects/documents/${fileId}/stream/`);
    return response.stream_url;
  },
};

// Telemetry - App events and GPS tracking
export type TelemetryEventType =
  | 'start_day'
  | 'end_day'
  | 'store_checkin'
  | 'store_checkout'
  | 'stop_driving'
  | 'location_update'
  | 'app_open'
  | 'app_close'
  | 'app_background'
  | 'app_foreground'
  | 'gps_activated'
  | 'gps_denied'
  | 'photo_captured'
  | 'document_uploaded'
  | 'odometer_entry';

export interface TelemetryEvent {
  event_type: TelemetryEventType;
  event_timestamp?: string; // ISO 8601 format
  lat?: number;
  lon?: number;
  gps_accuracy?: number;
  store_id?: number;
  calllog_id?: number;
  device_info?: {
    os?: string;
    app_version?: string;
    device_model?: string;
    battery_level?: number;
  };
}

export interface TelemetryRecord {
  telemetry_id: number;
  associate_id: number;
  associate_name: string | null;
  event_type: string;
  event_timestamp: string;
  lat: number | null;
  lon: number | null;
  gps_accuracy: number | null;
  store_id: number | null;
  store_name: string | null;
  calllog_id: number | null;
  device_info: Record<string, unknown> | null;
  received_at: string;
}

export interface LatestLocation {
  associate_id: number;
  name: string;
  city: string | null;
  state: string | null;
  region: string | null;
  active: boolean;
  home_lat: number | null;
  home_lon: number | null;
  last_event: {
    lat: number | null;
    lon: number | null;
    event_type: string | null;
    timestamp: string | null;
    store_name: string | null;
  } | null;
}

// ---------------------------------------------------------------------------
// Active-day rehydration (#3354). The server reconstructs the rep's current
// day (DayData shape, ISO date strings) from the #1950 event stream so a fresh
// device / reinstall / impersonation can resume it. Read-only.
// ---------------------------------------------------------------------------
export interface ServerLatLng {
  lat: number;
  lng: number;
}

export interface ServerTimeEntry {
  id: string;
  type: 'drive' | 'store';
  storeName?: string | null;
  storeId?: number | null;
  projectId?: number | null;
  startTime: string;
  endTime?: string | null;
  startLocation?: ServerLatLng | null;
  endLocation?: ServerLatLng | null;
  durationMinutes?: number | null;
  startOdometer?: number | null;
  endOdometer?: number | null;
  callLogId?: number | null;
  voided?: boolean;
}

export interface ServerDayData {
  status: 'active' | 'ended' | 'not_started';
  startTime: string | null;
  endTime: string | null;
  startLocation: ServerLatLng | null;
  currentLocation: ServerLatLng | null;
  currentActivity: 'idle' | 'driving' | 'in_store';
  currentStoreId: number | null;
  currentProjectId: number | null;
  timeEntries: ServerTimeEntry[];
  totalDriveMinutes: number;
  totalStoreMinutes: number;
  totalMiles: number;
}

export interface ActiveDayResponse {
  status: string;
  timezone: string;
  date: string;
  source: string;
  day: ServerDayData | null;
}

export const dayApi = {
  // Reconstruct the effective associate's current active day. Passes the
  // device tz so the server windows to the correct rep-local calendar day.
  getActiveDay: (signal?: AbortSignal) => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const q = tz ? `?tz=${encodeURIComponent(tz)}` : '';
    return apiFetch<ActiveDayResponse>(`/api/projects/events/active-day/${q}`, { signal });
  },
};

export const telemetryApi = {
  // Record a telemetry event
  recordEvent: (data: TelemetryEvent) =>
    apiFetch<{ status: string; telemetry_id: number; message: string }>('/api/associates/telemetry/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Get telemetry events with filtering
  getEvents: (params?: {
    associate_id?: number;
    event_type?: TelemetryEventType;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.associate_id) queryParams.append('associate_id', params.associate_id.toString());
    if (params?.event_type) queryParams.append('event_type', params.event_type);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    return apiFetch<TelemetryRecord[]>(`/api/associates/telemetry/list/?${queryParams.toString()}`);
  },

  // Get latest locations for all associates (for Rep Location page)
  getLatestLocations: (params?: { region_id?: number; active_only?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.region_id) queryParams.append('region_id', params.region_id.toString());
    if (params?.active_only !== undefined) queryParams.append('active_only', params.active_only.toString());
    return apiFetch<LatestLocation[]>(`/api/associates/telemetry/latest-locations/?${queryParams.toString()}`);
  },
};

// ============================================================================
// Routes API - Call Route Assignments with Frequency Tracking
// ============================================================================

export interface RouteStore {
  store_id: number;
  store_number: string;
  legacy_store_id: string | null;
  store_name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  retailer_id: number | null;
  retailer_name: string;
  retailer_abbreviation: string;
  lat: number | null;
  lon: number | null;
}

export interface Route {
  route_id: number;
  store: RouteStore;
  project_id: number;
  project_description: string;
  client_name: string | null;
  frequency: number;
  hours_per_visit: number | null;
  visits_completed: number;
  visits_remaining: number;
  last_visit_date: string | null;
  last_visit_id: number | null;
  /** Store coverage across every rep (#3454) — the schedule is per-associate,
   *  so a colleague's visit is otherwise invisible on this row. */
  visits_completed_store?: number;
  visits_completed_by_others?: number;
  completed_by?: { associate_id: number | null; name: string; count: number; last: string | null }[];
  // Per-project onsite requirements → Ord/Inv/OOS/Photo/Doc pills (#2214).
  requires_order?: boolean;
  requires_inventory?: boolean;
  requires_oos?: boolean;
  requires_photos?: boolean;
  requires_documents?: boolean;
}

export interface RouteSummary {
  total_stores: number;
  total_visits_required: number;
  total_visits_completed: number;
  total_visits_remaining: number;
  completion_percentage: number;
  /** The same totals counting every rep's work at these stores (#3454). */
  total_visits_completed_store?: number;
  total_visits_remaining_store?: number;
  completion_percentage_store?: number;
}

export interface MyRoutesResponse {
  status: string;
  message: string;
  billing_month: string;
  subproject_id: number | null;
  summary: RouteSummary;
  routes: Route[];
}

export interface SingleRouteResponse {
  status: string;
  message: string;
  route: Route;
}

export const routesApi = {
  // Get routes for current associate with frequency progress
  getMyRoutes: async (billingMonth?: string): Promise<MyRoutesResponse> => {
    const params = new URLSearchParams();
    if (billingMonth) params.append('billing_month', billingMonth);
    return apiFetch<MyRoutesResponse>(`/api/projects/routes/my-routes/?${params.toString()}`);
  },

  // Get a single route by ID
  getRouteById: async (routeId: number): Promise<SingleRouteResponse> => {
    return apiFetch<SingleRouteResponse>(`/api/projects/routes/${routeId}/`);
  },
};

// ============================================================================
// Available Calls API - Nearby Calls Bucket for Mobile
// ============================================================================

export interface AvailableCall {
  route_id: number;
  store: RouteStore;
  project_id: number;
  project_description: string;
  client_name: string | null;
  frequency: number;
  hours_per_visit: number | null;
  visits_completed: number;
  visits_remaining: number;
  last_visit_date: string | null;
  distance_miles: number | null;
  /** Store coverage across every rep (#3454) — the schedule is per-associate,
   *  so a colleague's visit is otherwise invisible on this row. */
  visits_completed_store?: number;
  visits_completed_by_others?: number;
  completed_by?: { associate_id: number | null; name: string; count: number; last: string | null }[];
  // Per-project onsite requirements → Ord/Inv/OOS/Photo/Doc pills (#2214).
  requires_order?: boolean;
  requires_inventory?: boolean;
  requires_oos?: boolean;
  requires_photos?: boolean;
  requires_documents?: boolean;
}

export interface AvailableCallsResponse {
  status: string;
  message: string;
  billing_month: string;
  anchor: { lat: number; lon: number } | null;
  total_available: number;
  calls: AvailableCall[];
}

export const availableCallsApi = {
  // Get available calls sorted by distance from anchor point
  getAvailableCalls: async (params?: {
    billing_month?: string;
    anchor_lat?: number;
    anchor_lon?: number;
    limit?: number;
  }): Promise<AvailableCallsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.billing_month) queryParams.append('billing_month', params.billing_month);
    if (params?.anchor_lat !== undefined) queryParams.append('anchor_lat', params.anchor_lat.toString());
    if (params?.anchor_lon !== undefined) queryParams.append('anchor_lon', params.anchor_lon.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    return apiFetch<AvailableCallsResponse>(`/api/projects/routes/available-calls/?${queryParams.toString()}`);
  },
};

// ============================================================================
// Store Tasks API - Multi-Task Store Visit Support
// ============================================================================

// Pending task at a store (could be a set or a call/route)
export interface PendingTask {
  task_type: 'set' | 'call';
  task_id: number;  // projectset_id for sets, route_id for calls
  project_id: number;
  project_description: string;
  client_name: string | null;
  distributor_id: number | null;
  // Per-project onsite workflow requirements (#2022). Optional — older payloads /
  // offline records may omit them (treated as "unconfigured" -> order entry).
  requires_order?: boolean;
  requires_inventory?: boolean;
  requires_oos?: boolean;
  // Per-project required attachments (#2077) — gate task finish on >=1 photo / doc.
  requires_photos?: boolean;
  requires_documents?: boolean;
  // #2092 checkout signature gate. REQUIRED BY DEFAULT: when absent (older
  // payload / offline cache) the PWA treats it as true and hides Skip Signature.
  requires_signature?: boolean;
  // #2951 — team-lead deliverable gating (SET tasks). is_team_project = the set
  // has rep1/rep2; is_current_user_lead = the current rep is the set's assigned
  // lead (associateid). On a team set, only the lead must complete the shared
  // deliverables (survey/photos/documents); non-leads just check in + record time.
  is_team_project?: boolean;
  is_current_user_lead?: boolean;
  // #2022 Phase 3 — distributor order rules (min total $ / max qty per line) so
  // the PWA can hard-enforce at capture time. Per-product order_increment travels
  // on the product cache, not here. Null when the distributor leaves them unset.
  min_order_amount?: number | null;
  max_product_count?: number | null;
  // Set-specific fields
  set_date?: string;
  set_hours?: number | null;
  window_start?: string;
  window_end?: string;
  notes?: string | null;
  // Call-specific fields
  frequency?: number;
  hours_per_visit?: number | null;
  visits_completed?: number;
  visits_remaining?: number;
  last_visit_date?: string | null;
  /** False when this call belongs to another rep's schedule (#3454). */
  assigned_to_me?: boolean;
  /** That rep's name, so the picker can say whose call it is. */
  assigned_to?: string | null;
  /** Store coverage across every rep (#3454) — the schedule is per-associate,
   *  so a colleague's visit is otherwise invisible on this row. */
  visits_completed_store?: number;
  visits_completed_by_others?: number;
  completed_by?: { associate_id: number | null; name: string; count: number; last: string | null }[];
}

export interface PendingTasksResponse {
  status: string;
  message: string;
  store_id: number;
  store_name: string;
  date: string;
  sets: PendingTask[];
  calls: PendingTask[];
}

// Completed task during a store visit (with timing)
export interface CompletedTask {
  task_type: 'set' | 'call';
  task_id: number;
  project_id: number;
  project_description: string;
  client_name?: string | null;
  start_time: string;  // ISO datetime
  end_time: string;    // ISO datetime
  duration_minutes: number;
  calllog_id?: number;  // If task had a CallLog created at check-in, include it here
  // #3022 — deterministic team config, carried from the worked task's IDB
  // session so checkout resolves team status from the completed task itself
  // (survives the set dropping out of pending / a reload).
  is_team_project?: boolean;
  is_current_user_lead?: boolean;
}

// Bulk call log creation request
export interface BulkCallLogTask {
  task_type: 'set' | 'call';
  task_id: number;
  project_id: number;
  // False when the rep left with this task paused/unfinished: it still logs the
  // rep's real hours but the server leaves the set open and excludes it from
  // client billing until finished. Defaults to true server-side when omitted.
  completed?: boolean;
  stcalltime: string;  // ISO datetime
  edcalltime: string;  // ISO datetime
  calllog_id?: number;  // If provided, UPDATE existing instead of CREATE new
  // #3020: stable per-task key (the IDB session uuid). Lets the server UPDATE
  // the row it already created on a retry instead of duplicating it.
  idempotency_key?: string;
}

export interface BulkCallLogRequest {
  store_id: number;
  tasks: BulkCallLogTask[];
  manager_name?: string;
  signature?: string;  // Base64 encoded
  no_signature_reason?: string;

  // Drive-TO data (applied to FIRST task only)
  stdrivetime?: string;      // ISO datetime - drive start
  eddrivetime?: string;      // ISO datetime - drive end (arrival at store)
  driveodostart?: number;    // Odometer at drive start
  driveodoend?: number;      // Odometer at arrival

  // Drive-BACK data (applied to LAST task only, optional)
  stdrivetimeback?: string;  // ISO datetime - return drive start
  eddrivetimeback?: string;  // ISO datetime - return drive end
  drivebackodostart?: number;
  drivebackodoend?: number;

  // GPS data (applied to all tasks)
  lat?: number;
  lon?: number;
  gps_accuracy?: number;
  gps_override_reason?: string;

  // Other shared fields
  notes?: string;
  teamproject?: boolean;
  teamleader?: boolean;
}

export interface BulkCallLogResponse {
  status: string;
  message: string;
  data: {
    calllogid: number;
    task_type: string;
    task_id: number;
    project_id: number;
    stcalltime: string;
    edcalltime: string;
    // #3020: echoes the task's idempotency_key (IDB session uuid) so the client
    // can write the server calllogid back onto the right session.
    idempotency_key?: string | null;
  }[];
  errors?: string[];
}

export const storeTasksApi = {
  // Get pending tasks (sets and calls) for a specific store
  // projectId is needed for disambiguation when multiple stores share the same legacystoreid
  /**
   * Tasks pending at a store. `includeAllReps` widens the list to calls
   * assigned to other reps, so someone standing in the store can cover for a
   * colleague (#3454). Recording the work never reassigns the schedule.
   */
  getPendingTasks: async (
    storeId: number,
    date?: string,
    projectId?: number,
    includeAllReps?: boolean,
  ): Promise<PendingTasksResponse> => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (projectId) params.append('projectId', projectId.toString());
    if (includeAllReps) params.append('include_all_reps', 'true');
    return apiFetch<PendingTasksResponse>(`/api/retailers/stores/${storeId}/pending-tasks/?${params.toString()}`);
  },

  // Create multiple call logs from a single store visit
  bulkCreateCallLogs: async (data: BulkCallLogRequest): Promise<BulkCallLogResponse> => {
    return apiFetch<BulkCallLogResponse>('/api/projects/calllog/bulk/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ============================================================================
// Sets API - Scheduled Display Installations
// ============================================================================

export interface SetTeamMember {
  associate_id: number;
  name: string;
  is_current_user: boolean;
  role: 'leader' | 'rep1' | 'rep2';
}

export interface DisplaySpecs {
  chart: number | null;
  set_window: string | null;
  window_start: string | null;  // Parsed start date (YYYY-MM-DD)
  window_end: string | null;    // Parsed end date (YYYY-MM-DD)
  disp_alloc: string | null;
  inl_wide: number | null;
  inl_high: number | null;
  burn: string | null;
  prev_disp: string | null;
}

export interface ScheduledSet {
  projectset_id: number;
  project_id: number;
  project_description: string;
  client_name: string | null;
  distributor_id: number | null;
  store: RouteStore;
  set_date: string;
  set_hours: number | null;
  set_cost: number | null;
  set_type_id: number | null;
  set_type_name: string | null;
  notes: string | null;
  team: SetTeamMember[];
  display_specs: DisplaySpecs;
  status: 'pending' | 'completed';
  completed_date: string | null;
  actual_hours: number | null;
  // Per-project onsite requirements → Ord/Inv/OOS/Photo/Doc pills (#2214).
  requires_order?: boolean;
  requires_inventory?: boolean;
  requires_oos?: boolean;
  requires_photos?: boolean;
  requires_documents?: boolean;
}

export interface MySetsResponse {
  status: string;
  message: string;
  count: number;
  sets: ScheduledSet[];
}

export interface SingleSetResponse {
  status: string;
  set: ScheduledSet;
}

export const setsApi = {
  // Get sets for current associate (as leader, rep1, or rep2)
  getMySets: async (params?: {
    start_date?: string;
    end_date?: string;
    status?: 'pending' | 'completed' | 'all';
  }): Promise<MySetsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.status) queryParams.append('status', params.status);
    return apiFetch<MySetsResponse>(`/api/projects/sets/my-sets/?${queryParams.toString()}`);
  },

  // Get a single set by ID (much faster than getMySets + filter)
  getSetById: async (setId: number): Promise<SingleSetResponse> => {
    return apiFetch<SingleSetResponse>(`/api/projects/sets/${setId}/`);
  },
};

// Impersonation types
export interface ImpersonationAssociate {
  associateid: number;
  name: string;
  email: string;
  city: string;
  state: string;
  set_count: number;
  route_count: number;
  data_score: number;
}

export interface ImpersonationStatus {
  associateid: number;
  name: string;
}

export interface ImpersonationListResponse {
  status: string;
  can_impersonate: boolean;
  current_impersonation: ImpersonationStatus | null;
  associates: ImpersonationAssociate[];
}

/**
 * Shape returned by /api/auth/impersonate/set|clear/ that the PWA caches in
 * localStorage.associate so subsequent API payloads carry the now-effective
 * associate id (#1499).
 */
export interface ImpersonationAssociatePayload {
  associateid: number;
  firstname: string;
  lastname: string;
  email?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  telephone?: string | null;
}

export interface ImpersonationSetResponse {
  status: string;
  message: string;
  impersonating?: ImpersonationStatus;
  /** Effective associate after the toggle. Frontend writes to localStorage.associate. */
  associate?: ImpersonationAssociatePayload | null;
}

// Impersonation API
export const impersonationApi = {
  // Get list of associates available for impersonation (superusers only).
  // Optional q filters across firstname/lastname/email/associateid so the
  // searchable picker (#1891) can find anyone, not just the activity-top-50.
  getList: async (q?: string): Promise<ImpersonationListResponse> => {
    const trimmed = (q ?? '').trim();
    const url = trimmed
      ? `/api/auth/impersonate/list/?q=${encodeURIComponent(trimmed)}`
      : '/api/auth/impersonate/list/';
    return apiFetch<ImpersonationListResponse>(url);
  },

  // Set impersonation to a specific associate
  setImpersonation: async (associateid: number): Promise<ImpersonationSetResponse> => {
    return apiFetch<ImpersonationSetResponse>(`/api/auth/impersonate/set/${associateid}/`, {
      method: 'POST',
    });
  },

  // Clear impersonation
  clearImpersonation: async (): Promise<ImpersonationSetResponse> => {
    return apiFetch<ImpersonationSetResponse>('/api/auth/impersonate/clear/', {
      method: 'POST',
    });
  },
};

// Admin destructive actions (#1964) — superuser-only. POSTs from the
// Profile "Hard Reset Day" admin button. The PWA side pairs the server
// void with a local IDB+caches+SW nuke and a forced re-login.
export interface AdminResetRepDayResponse {
  status: 'success' | 'error';
  associate_id?: number;
  date?: string;
  scope?: 'OPEN_ONLY' | 'FULL_DAY';
  voided?: number;
  voided_ids?: number[];
  message?: string;
}

export const adminApi = {
  /**
   * Server-side void of an associate's day. Required prerequisite for a
   * true local-side reset — without voiding the open CallLogs, the PWA
   * hydrates them right back from the server on next load.
   *
   * @param associateId — target rep (the impersonated one for testers)
   * @param options.forceFullDay — also void closed CallLogs (default: open-only)
   *
   * #3026 — always sends the device's LOCAL-day window (dayStart/dayEnd) so the
   * server voids exactly the day the rep sees, not the server's UTC calendar day
   * (an ET rep near the UTC midnight boundary was voiding the wrong day).
   */
  resetRepDay: async (
    associateId: number,
    options: { forceFullDay?: boolean } = {},
  ): Promise<AdminResetRepDayResponse> => {
    const now = new Date();
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    return apiFetch<AdminResetRepDayResponse>('/api/projects/admin/reset-rep-day/', {
      method: 'POST',
      body: JSON.stringify({
        associate_id: associateId,
        day_start: dayStart.toISOString(),
        day_end: dayEnd.toISOString(),
        force_full_day: options.forceFullDay ?? false,
      }),
    });
  },
};

// Diagnostics submission - #1847
export interface DiagnosticsSubmitResponse {
  ok: boolean;
  report_id: number;
  received_at: string;
}

export const diagnosticsApi = {
  submit: async (payload: unknown): Promise<DiagnosticsSubmitResponse> => {
    return apiFetch<DiagnosticsSubmitResponse>('/api/diagnostics/submit/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a call log entry has been signed by a manager.
 * Uses the API's computed has_signature field first (more reliable for list responses),
 * with fallback to checking signature/no_signature_reason for backward compatibility.
 */
export function isVisitSigned(callLog: CallLog): boolean {
  // True manager signature captured — NOT Skip Signature with a reason.
  // The server's computed has_signature flag is true for EITHER case, so
  // we combine it with the negative of isVisitSkipped. That lets us still
  // use has_signature (which the list endpoint sets) while the raw
  // `signature` blob is omitted from list responses for payload size.
  // (#1471 follow-up #4 — previously the green "Signed" badge rendered
  // for every Skip Signature visit.)
  if (isVisitSkipped(callLog)) return false;
  if (typeof callLog.has_signature === 'boolean') return callLog.has_signature;
  return !!callLog.signature;
}

/**
 * Check if a visit was deliberately checked out without a manager signature
 * (e.g., manager unavailable, store closed early). Renders as a distinct
 * badge from the green "Signed" check so the rep + timesheet reviewers can
 * see at a glance that the rep completed the visit but no manager was
 * around to sign.
 */
export function isVisitSkipped(callLog: CallLog): boolean {
  return !!callLog.no_signature_reason;
}

/**
 * Edit reason options for editing signed entries.
 */
export const EDIT_REASONS = [
  { value: 'time_correction', label: 'Time correction' },
  { value: 'mileage_correction', label: 'Mileage correction' },
  { value: 'adding_notes', label: 'Adding notes' },
  { value: 'team_info_update', label: 'Team info update' },
  { value: 'other', label: 'Other' },
] as const;

export type EditReasonValue = typeof EDIT_REASONS[number]['value'];

// ---------------------------------------------------------------------------
// Rep activity event stream (#1950) — batched, idempotent.
//
// The PWA event sync engine calls postEvents() once per drain cycle with
// up to MAX_EVENTS_PER_BATCH (200, server-enforced) records. The server
// returns a per-event status in the same order, so the sync engine can
// distinguish "accepted" / "already had it" / "invalid (don't retry)" /
// "error (retry later)".
// ---------------------------------------------------------------------------

export interface EventPayloadForServer {
  client_event_id: string;
  event_type: string;
  event_at: string;
  store_id?: number | null;
  route_id?: number | null;
  project_id?: number | null;
  calllog_id?: number | null;
  correction_of?: number | null;
  /** Supersede a punch this device emitted; the server resolves it to an id. */
  correction_of_client_event_id?: string | null;
  payload?: Record<string, unknown>;
}

export interface EventBatchResultItem {
  client_event_id: string | null;
  server_event_id: number | null;
  status: 'created' | 'duplicate' | 'invalid' | 'error';
  errors?: Record<string, unknown>;
}

export interface EventBatchResponse {
  status: 'success' | 'error';
  message?: string;
  results: EventBatchResultItem[];
}

export interface EventSummaryResponse {
  status: string;
  date: string;
  timezone: string;
  counts: Record<string, number>; // event_type -> server-confirmed count for the day
}

export const eventsApi = {
  /**
   * POST a batch of events to the server. Returns the per-event ack list.
   * Network errors propagate untouched — the sync engine handles them.
   */
  postBatch: async (events: EventPayloadForServer[]): Promise<EventBatchResponse> => {
    return apiFetch<EventBatchResponse>('/api/projects/events/', {
      method: 'POST',
      body: JSON.stringify({ events }),
    });
  },

  /**
   * Authoritative per-event-type counts for the rep's day (#1950 Phase 2) —
   * the device-independent "synced" denominator for the chip's synced/total view.
   */
  getSummary: async (date: string, tz: string): Promise<EventSummaryResponse> => {
    const qs = new URLSearchParams({ date, tz }).toString();
    return apiFetch<EventSummaryResponse>(`/api/projects/events/summary/?${qs}`);
  },
};

export default {
  associates: associatesApi,
  projects: projectsApi,
  stores: storesApi,
  callLogs: callLogsApi,
  documents: documentsApi,
  surveys: surveysApi,
  products: productsApi,
  orders: ordersApi,
  expenses: expensesApi,
  categoryDocuments: categoryDocumentsApi,
  telemetry: telemetryApi,
  routes: routesApi,
  availableCalls: availableCallsApi,
  sets: setsApi,
  storeTasks: storeTasksApi,
  impersonation: impersonationApi,
  events: eventsApi,
  admin: adminApi,
};
